import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { landmarkPointStructures } from '~/domain/landmark/landmarkPointStructures';
import { RoiGenerationAlgorithmType } from '~/domain/dicom/dicomstructureset';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import { landmarkPairListState } from '~/state/atoms/landmarkAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { enqueueSnackbar } from '~/utils/notifications';

export function useAddLandmarksToStructureSet(): {
    readonly count: number;
    readonly add: () => Promise<void>;
} {
    const intl = useIntl();
    const session = useSessionOrNull();
    const pairs = useRecoilValue(landmarkPairListState);
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const { createNewRoi } = useStructureSet();

    const namePrefix = intl.formatMessage({ id: 'landmark.pointStructure' });
    const structures = useMemo(
        () =>
            landmarkPointStructures(
                pairs,
                // The set's own frame, not the primary image's: the set may belong to another scan.
                currentStructureSet?.frameOfReferenceUid,
                namePrefix
            ),
        [pairs, currentStructureSet?.frameOfReferenceUid, namePrefix]
    );

    const add = useCallback(async () => {
        const structureSetUid = currentStructureSet?.dicomUid;
        if (!structureSetUid || structures.length === 0) {
            return;
        }
        if (
            currentStructureSet.approvalStatus !==
            DICOMApprovalStatus.UNAPPROVED
        ) {
            enqueueSnackbar(
                intl.formatMessage({ id: 'landmark.pointStructuresLocked' }),
                { variant: 'warning' }
            );
            return;
        }
        let written = 0;
        for (const structure of structures) {
            const roi = await createNewRoi(
                structureSetUid,
                structure.name,
                RoiInterpretedType.REGISTRATION,
                false,
                {
                    generation: {
                        algorithm: RoiGenerationAlgorithmType.MANUAL,
                        description: 'Registration landmark placed by the user',
                    },
                }
            );
            if (!roi) {
                break;
            }
            session?.roiService.setPointCoordinates(
                { id: roi.id },
                structure.pointMm
            );
            written++;
        }
        enqueueSnackbar(
            intl.formatMessage(
                {
                    id:
                        written === structures.length
                            ? 'landmark.pointStructuresAdded'
                            : 'landmark.pointStructuresPartial',
                },
                { count: written, total: structures.length }
            ),
            { variant: written === structures.length ? 'success' : 'warning' }
        );
    }, [
        currentStructureSet?.dicomUid,
        currentStructureSet?.approvalStatus,
        structures,
        createNewRoi,
        session,
        intl,
    ]);

    return {
        count: currentStructureSet?.dicomUid ? structures.length : 0,
        add,
    };
}
